import React, { useState } from "react";
import { Star } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function formatVotes(n) {
  if (!n) return "0";
  return n.toLocaleString();
}

export default function StarVote({ rank, voteCount, avgRating, displayCount, onVoted }) {
  const [hover, setHover] = useState(0);
  const [selected, setSelected] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [thanked, setThanked] = useState(false);

  const submit = async (rating) => {
    if (submitting) return;
    setSelected(rating);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/slots/${rank}/vote`, { rating });
      toast.success(data.message || "Thank you for submitting your vote!", {
        description: `You rated #${rank} ${rating} star${rating > 1 ? "s" : ""}.`,
        duration: 4000,
      });
      setThanked(true);
      onVoted && onVoted(data);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg, { duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const stars = [1, 2, 3, 4, 5];
  const showRating = hover || selected || Math.round(avgRating || 0);

  return (
    <div className="space-y-2" data-testid={`star-vote-${rank}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
          {stars.map((n) => {
            const filled = n <= showRating;
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onClick={() => submit(n)}
                disabled={submitting}
                aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                className={`p-1 rounded transition-transform duration-200 ${
                  filled ? "text-neon-cyan" : "text-zinc-700"
                } hover:scale-110 disabled:opacity-60`}
                data-testid={`star-${rank}-${n}`}
              >
                <Star size={22} weight={filled ? "fill" : "regular"} />
              </button>
            );
          })}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-lg font-bold text-neon-cyan tabular-nums" data-testid={`vote-count-${rank}`}>
            {formatVotes(displayCount)}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
            heat points
          </span>
        </div>
        {voteCount > 0 && (
          <span className="text-xs text-zinc-500 font-mono" data-testid={`avg-rating-${rank}`}>
            avg {avgRating?.toFixed(1) || "0.0"}/5
          </span>
        )}
      </div>
      <p className={`text-xs transition-opacity duration-300 ${thanked ? "text-neon-cyan" : "text-zinc-500"}`}>
        {thanked
          ? "Thanks for casting your vote — come back in 2 hours to vote again."
          : "Tap a star to cast your vote — each star adds 100 to the heat meter (max 500 per voter)."}
      </p>
    </div>
  );
}
